import type { EventFormData } from './EventFormContainer';
import { normalizeCustomFieldScope } from '../../utils/customFieldTicketScope';

export function parseEventDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function mapLegacyTickets(tickets: unknown[]): EventFormData['tickets'] {
  return (tickets || []).map((ticket: any) => ({
    _id: ticket._id || ticket.id,
    name: ticket.name || '',
    description: ticket.description || '',
    price: ticket.price ?? 0,
    quantity: ticket.quantity ?? 0,
    startSaleDate: parseEventDate(ticket.startSaleDate),
    endSaleDate: parseEventDate(ticket.endSaleDate),
    showPlatformFee: ticket.showPlatformFee || false,
    isRestricted: ticket.isRestricted || false,
    allowedEmails: ticket.allowedEmails || [],
    minPerOrder: ticket.minPerOrder ?? 1,
    maxPerOrder: ticket.maxPerOrder ?? null,
    allowSameAttendeeData: ticket.allowSameAttendeeData !== false,
    isActive: ticket.isActive !== undefined ? ticket.isActive : true,
    hiddenFromPublicPage: ticket.hiddenFromPublicPage || false,
  }));
}

function mapTicketTypes(ticketTypes: unknown[]): EventFormData['ticketTypes'] {
  return (ticketTypes || []).map((type: any) => ({
    ...type,
    batches: (type.batches || []).map((batch: any) => ({
      ...batch,
      startSaleDate: parseEventDate(batch.startSaleDate),
      endSaleDate: parseEventDate(batch.endSaleDate),
    })),
  }));
}

function mapSalesPhases(salesPhases: unknown[]): EventFormData['salesPhases'] {
  return (salesPhases || []).map((phase: any) => ({
    ...phase,
    startSaleDate: parseEventDate(phase.startSaleDate),
    endSaleDate: parseEventDate(phase.endSaleDate),
  }));
}

function mapProducts(products: unknown[]): EventFormData['products'] {
  return (products || []).map((product: any) => ({
    name: product.name || '',
    description: product.description || '',
    price: product.price ?? 0,
    quantity: product.quantity ?? 0,
    image: typeof product.image === 'string' ? product.image : null,
    startSaleDate: parseEventDate(product.startSaleDate),
    endSaleDate: parseEventDate(product.endSaleDate),
  }));
}

function mapCustomFields(customFields: unknown[]): EventFormData['customFields'] {
  return (customFields || []).map((field: any, idx: number) => {
    const normalized = normalizeCustomFieldScope(field);
    return {
      id: field._id || field.id || `field-${idx}-${Date.now()}`,
      fieldName: field.fieldName || field.name || '',
      fieldType: (field.fieldType || field.type || 'text') as EventFormData['customFields'][0]['fieldType'],
      isRequired: field.isRequired ?? field.required ?? false,
      options: field.options || [],
      appliesToAllTickets: normalized.appliesToAllTickets,
      ticketScopeIds: normalized.ticketScopeIds || [],
      useForSegmentation: field.useForSegmentation === true,
    };
  });
}

/** Converte resposta da API (workspace ou admin) para o formato do EventFormContainer. */
export function mapApiEventToFormData(event: Record<string, any>): Partial<EventFormData> {
  return {
    _id: event._id,
    title: event.title || '',
    slug: event.slug || '',
    description: event.description || '',
    startDate: parseEventDate(event.startDate) ?? null,
    endDate: parseEventDate(event.endDate) ?? null,
    location: event.location || '',
    mapLink: event.mapLink || '',
    address: event.address || '',
    city: event.city || '',
    state: event.state || '',
    category: event.category || 'other',
    capacity: event.capacity ?? 100,
    showRemainingSpots: event.showRemainingSpots === true,
    isPaid: event.isPaid || false,
    ticketCatalogMode: event.ticketCatalogMode || 'legacy',
    tickets: mapLegacyTickets(event.tickets || []),
    ticketTypes: mapTicketTypes(event.ticketTypes || []),
    salesPhases: mapSalesPhases(event.salesPhases || []),
    ticketLinks: Array.isArray(event.ticketLinks) ? event.ticketLinks : [],
    hasStore: event.hasStore || false,
    products: mapProducts(event.products || []),
    registrationDeadline: parseEventDate(event.registrationDeadline) ?? new Date(),
    customFields: mapCustomFields(event.customFields || []),
    coverImage: event.coverImage || null,
    galleryImages: Array.isArray(event.galleryImages) ? event.galleryImages : [],
    contactEmail: event.contactEmail || '',
    contactPhone: event.contactPhone || '',
    showMap: event.showMap !== undefined ? event.showMap : true,
    showSocialShare: event.showSocialShare !== undefined ? event.showSocialShare : true,
    customTheme: event.customTheme || {
      primaryColor: '#3f51b5',
      secondaryColor: '#f50057',
      fontFamily: 'Roboto',
    },
    pageTemplate: event.pageTemplate || 'default',
    postRegistrationUrl: event.postRegistrationUrl || '',
    status: event.status || 'pre_published',
    allowWaitlist: event.allowWaitlist,
    allowGroupRegistration: event.allowGroupRegistration,
    requireApproval: event.requireApproval,
    sendConfirmationEmail: event.sendConfirmationEmail,
    allowInstallments: event.allowInstallments,
  };
}
